import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {classes, style, vars} from './bymo-page-viewer.st.css';
import type {BymoPageContextProps} from '../../doppe-sdk';
import {BymoPageContextPropsContext} from '../../doppe-sdk';
import type {BymoPageProps, BymoPagePropsForViewer} from '../../client-server-common';
import {
    BymoPagePropsContext,
    bymoPagePropsFindActionById,
    bymoPagePropsResolveProfileNameFontStyleSheetUrl,
    bymoPagePropsResolveTextFontFamilyStyleSheetUrl,
    bymoTemplateDesktopLayoutEnsureSupportedLayout,
    DoppeBiEmptyTargetUrl,
    doppeViewerPageInfoResolvePageId
} from '../../client-server-common';
import {
    BymoPageViewerMode,
    bymoPageViewerModeIsPreview,
    useBymoPageViewerContext
} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import type {DoppeViewerPageInfo} from '../../client-server-common/viewer-pages/doppe-viewer-pages';
import {doppeViewerPageInfoCreateHome} from '../../client-server-common/viewer-pages/doppe-viewer-pages';
import {
    ListViewIdsProvider,
    ModalsScope,
    reactCreateMemo,
    reactProvideContextValues,
    SsrSafeHtmlIdsProvider,
    useEqualityCheckedMemo,
    useEventCallback,
    useLayoutBinding,
    useObservableValue,
    useRootWindow,
    useWindowDependantMemoWithDefaultValue
} from '@wix/devzai-utils-react';
import {DomEventListener, domGetScrollbarSize, domLoadCSS} from '@wix/devzai-utils-dom';
import {
    arrayDistinct,
    arrayLast,
    arrayRemoveNullishValues,
    assertNotNullable, cssAddUnitsToNumericValue,
    immerProduce,
    objectGetEntries,
    objectShallowEqual,
    referencesEqualityComparer
} from '@wix/devzai-utils-common';
import {BymoPageStyleContext, bymoPageStyleResolveVars} from '../bymo-page-style/bymo-page-style';
import {BrowserHistory} from '@wix/devzai-common-client';
import {
    DoppeViewerColorPaletteCalculatedColorsDataReactContext,
    DoppeViewerHtmlSiteInfoReactContext,
    DoppeViewerLangReactContext,
    DoppeViewerPageDesktopLayoutReactContext,
    DoppeViewerPageStandardDesktopLayoutContentWidthReactContext,
    DoppeViewerShouldShowCookieBannerReactContext,
    DoppeViewerShouldShowPrivacyAndTermsReactContext
} from '../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import {BymoPageRoot} from "../bymo-page-root/bymo-page-root";
import {useDoppeViewerClientServices} from "../../client/doppe-user-app/doppe-viewer-client-services-react";
import {BymoPageViewerApi, BymoPageViewerApiReactContext} from "./bymo-page-viewer.types";
import {
    DoppeViewerModalsController,
    DoppeViewerModalOpeningAnimation
} from '../doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import {DoppeViewerModalPanel, DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {
    DoppeActionModalIframeContent
} from '../doppe-action-modal-iframe-content/doppe-action-modal-iframe-content';
import {
    DoppeViewerHorizontalButton,
    DoppeViewerHorizontalButtonPriority
} from '../doppe-viewer-horizontal-button/doppe-viewer-horizontal-button';
import {DoppeViewerAppIconDismissMedium} from '../doppe-viewer-icons/doppe-viewer-icons';
import {flexColumn, flexFillRemainingVerticalSpace} from '@wix/devzai-utils-react';
import {BymoPageStyleScope} from '../../client-server-common';
import {BymoPageStyleScopeContext} from '../bymo-page-style/bymo-page-style';

const Features = {
    enableBackInterception: true
}

export const BymoPageViewer = reactCreateMemo(function BymoPageViewer(props: BymoPageViewer.Props) {
    const {
        bymoPageComponentType,
        bymoPagePropsForViewer,
        bymoPageContextProps,
        currentPageInfo: initialPageInfo
    } = props;

    const pageViewerContext = useBymoPageViewerContext();
    const clientServices = useDoppeViewerClientServices();

    // Sync with initialPageInfo prop changes (from editor panel navigation)
    // This effect ONLY runs in Preview/Editor modes, NOT in Live mode
    useEffect(() => {
        // This sync effect is ONLY for preview/editor modes
        // In Live mode, the router is the source of truth (not the prop)
        if (!bymoPageViewerModeIsPreview(pageViewerContext.mode)) {
            // Skip router navigation in Live mode - router controls navigation
            return;
        }


        // Use initialPageInfo directly (no conversion needed)
        const router = clientServices.viewerRouterClientService;
        const pageInfo = initialPageInfo ?? doppeViewerPageInfoCreateHome();
        const currentPageInfo = router.getCurrentPageInfo();

        // Only navigate if the page info has actually changed (prevents redundant navigation)
        if (!router.doppeViewerPageInfoEquals(currentPageInfo, pageInfo)) {
            router.navigateToPageInfo(pageInfo, { replace: true });
        }

    }, [initialPageInfo, pageViewerContext.mode, clientServices.viewerRouterClientService]);




    // Subscribe to router page changes and emit events to editor
    // In LivePreview mode: emit page navigation events to editor
    useEffect(() => {
        const router = clientServices.viewerRouterClientService;

        const reportPageView = () => {
            if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                const currentPageInfo = router.getCurrentPageInfo();
                const innerPageId = doppeViewerPageInfoResolvePageId(currentPageInfo, bymoPagePropsForViewer) ?? undefined;

                clientServices.viewerAnalyticsClient.logEvent('uouPageViewed', {
                    pageId: pageViewerContext.bymoPageId,
                    hypeUserId: pageViewerContext.doppeUserId,
                    linkId: pageViewerContext.linkId,
                    destinationUrl: bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: pageViewerContext.pageType,
                    innerPageId: innerPageId,
                });

                clientServices.viewerBmAnalyticsDashboardBiClient.logEvent('pageView', {
                    url: window.location.href,
                    ref: document.referrer,
                    msid: clientServices.bymoService.getBymoLinkViewerData()?.creatorsMetaSiteId ?? '',
                    vid: clientServices.doppeViewerClientData.clientId,
                    vsi: clientServices.doppeViewerClientData.sessionId,
                    bsi: clientServices.doppeViewerClientData.sessionId,
                    viewer: 'unknown',
                });
            }
        }
        const unsubscribe = router.subscribe((pageInfo) => {

            //TODO:: @nird please review this fix. when navigating to a page and a modal is opened, this rule is closing all opened modals.
            // for example i open the menu drawer clicks on the serach and then clicks on a product block with cta link button the navigate to the product page. clicking the button will close all modals.
            const openModals = [...(modalsScopeRef.current?.getOpenModals() ?? [])];
            for (const modal of openModals) {
                modal.closeWindowImmediately();
            }

            // Emit event to editor in LivePreview mode (not in static Preview mode)
            if (pageViewerContext.mode === BymoPageViewerMode.LivePreview) {
                pageViewerContext.bymoPagePreviewHostServiceApiClient.emitBymoPagePreviewEvent('eventPageNavigationChanged', {
                    pageInfo: pageInfo
                });

            }

            if (!bymoPageViewerModeIsPreview(pageViewerContext.mode)) {
                reportPageView();
            }
        });

        reportPageView();

        return unsubscribe;
    }, [bymoPageContextProps.targetUrl, clientServices, pageViewerContext]);

    const rootWindow = useRootWindow();
    const modalsScopeRef = useRef<ModalsScope>(null);

    useLayoutEffect(() => {
        if (Features.enableBackInterception && pageViewerContext.mode !== BymoPageViewerMode.LivePreview) {
            const disposeBrowserHistoryInterception = BrowserHistory.interceptNavigation((_, action) => {

                if (action === 'POP') {
                    const topMostModal = arrayLast(modalsScopeRef.current?.getOpenModals() ?? []);

                    if (topMostModal) {
                        return BrowserHistory.interceptionAction(() => {
                            topMostModal.closeWindow();
                        });
                    }
                }

                return false;
            }, Infinity)

            return () => {
                disposeBrowserHistoryInterception();
            }
        } else {
            return undefined;
        }
    }, []);

    // Register modalsScope with uiService for cross-component modal access
    useLayoutEffect(() => {
        if (modalsScopeRef.current) {
            clientServices.uiService.setComponentInstance('modalsScope', modalsScopeRef.current);
        }
        return () => {
            clientServices.uiService.setComponentInstance('modalsScope', null);
        };
    }, [clientServices]);

    const actionsSettingsOverrides = useObservableValue(clientServices.bymoService.observableActionsSettingsOverrides);

    const bymoPageProps = useMemo(() => {
        return immerProduce(bymoPagePropsForViewer, (bymoPagePropsForViewer) => {
            objectGetEntries(actionsSettingsOverrides).forEach(([actionId, actionSettings]) => {
                const action = bymoPagePropsFindActionById(bymoPagePropsForViewer, actionId);
                if (action) {
                    // TODO: Make sure that actionSettings are not partial - contain the full data.
                    action.settings = actionSettings;
                }
            });
        });
    }, [bymoPagePropsForViewer, actionsSettingsOverrides]);

    const fontStyleSheetUrls = useMemo(() => {
        return arrayDistinct(arrayRemoveNullishValues([
            bymoPagePropsResolveTextFontFamilyStyleSheetUrl(bymoPageProps),
            bymoPagePropsResolveProfileNameFontStyleSheetUrl(bymoPageProps),
        ]));
    }, [bymoPageProps]);

    useEffect(() => {
        if (pageViewerContext.mode !== BymoPageViewerMode.Live) {
            fontStyleSheetUrls.forEach(url => {
                domLoadCSS(url, {rootWindow: rootWindow})
                    .catch(error => {
                        console.error(`Failed loading font css`, error)
                    })
            });
        }
    }, [fontStyleSheetUrls])

    const hideBrowserScrollbar = bymoPageViewerModeIsPreview(pageViewerContext.mode);
    useEffect(() => {
        const currentWindow = (rootWindow ?? window);

        currentWindow.document.documentElement.classList.toggle(
            classes.hideBrowserScrollbar,
            hideBrowserScrollbar || (currentWindow.self !== currentWindow.top)
        );

    }, [hideBrowserScrollbar, rootWindow])

    const bymoPageNamespacedStyleVarsValues = useMemo(() => {
        return bymoPageStyleResolveVars({
            lang: bymoPageProps.lang,
            colorPalette: bymoPageProps.colorPalette,
            textFontFamily: bymoPageProps.textFontFamily,
            devCustomPageContentWidthInWideLayout: bymoPageProps.devCustomPageContentWidthInWideLayout,
        })
    }, [
        bymoPageProps.lang,
        bymoPageProps.colorPalette,
        bymoPageProps.textFontFamily,
        bymoPageProps.devCustomPageContentWidthInWideLayout
    ]);

    const lastScrollTopRef = useRef<number>(0);

    const isIos = useWindowDependantMemoWithDefaultValue(
        window => /iPhone|iPad|iPod/.test(window.navigator.userAgent) || true,
        false,
        []
    );

    const bymoPageRootRef = useRef<HTMLDivElement>(null);

    const [modalIsShown, setModalIsShown] = useState(false);

    const visualViewportHeight = useLayoutBinding(
        useCallback(() => (rootWindow ?? window).visualViewport?.height, [rootWindow]),
        referencesEqualityComparer,
        isIos && modalIsShown
    );

    const isModalShownOnIos = isIos && modalIsShown;

    useEffect(() => {
        if (isModalShownOnIos) {
            const targetWindow = rootWindow ?? window;

            let ignoreScrolling = false;
            const touchStartBinding = DomEventListener.bind(targetWindow.document, 'touchstart', () => {
                ignoreScrolling = true;
            })

            let scheduledScroll: any | undefined = undefined;
            const scheduleScroll = () => {
                clearTimeout(scheduledScroll);
                scheduledScroll = setTimeout(() => {
                    targetWindow.scrollTo({
                        top: 0
                    })
                }, 16)
            }


            const touchEndBinding = DomEventListener.bind(targetWindow.document, 'touchend', () => {
                ignoreScrolling = false;
                scheduleScroll();
            })

            const binding = DomEventListener.bind(targetWindow, 'scroll', () => {
                if (!ignoreScrolling) {
                    scheduleScroll();
                }
            })

            return () => {
                clearTimeout(scheduledScroll);
                binding.dispose();
                touchStartBinding.dispose();
                touchEndBinding.dispose();
            }
        } else {
            return undefined;
        }
    }, [isModalShownOnIos, rootWindow])

    useLayoutEffect(() => {
        (rootWindow ?? window).document.documentElement.style.setProperty(
            vars.visualViewportHeight,
            isIos && visualViewportHeight ? `${visualViewportHeight}px` : `100%`
        );
    }, [visualViewportHeight, isIos])

    useEffect(() => {
        return () => {
            const currentWindow = (rootWindow ?? window);
            const document = currentWindow.document;
            document.documentElement.classList.toggle(classes.modalShown, false);
            document.documentElement.classList.toggle(classes.modalShownWithClassicScrollbar, false);
        }
    }, [rootWindow]);

    // Handle iframeModalURL query parameter - open URL in inner centered iframe modal
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const iframeModalURL = urlParams.get('iframeModalURL');
        
        if (iframeModalURL && modalsScopeRef.current) {
            clientServices.cartService.handleCheckoutRedirect();

            const modalsController = new DoppeViewerModalsController({
                modalsScope: modalsScopeRef.current,
                unmountingTaskSchedulerGetter: null
            });
            
            // Open the URL in an inner centered iframe modal with custom sizing
            modalsController.showInnerModal({
                openingAnimation: DoppeViewerModalOpeningAnimation.FadeIn,
                content: ({modalController}) => (
                    <BymoPageStyleScopeContext.Provider value={BymoPageStyleScope.Popup}>
                        <DoppeViewerModalPanel
                            className={style(classes.iframeModalPanel, {}, flexColumn())}
                            mobileLayout={DoppeViewerModalPanelLayout.InnerCentered}
                            desktopLayout={DoppeViewerModalPanelLayout.InnerCentered}
                        >
                            <DoppeViewerHorizontalButton
                                prefixIcon={DoppeViewerAppIconDismissMedium}
                                textual={true}
                                priority={DoppeViewerHorizontalButtonPriority.Secondary}
                                onClick={() => {
                                    modalController.closeWindow();
                                }}
                                style={{alignSelf: 'flex-end', margin: '8px'}}
                            />
                            <div className={flexFillRemainingVerticalSpace()}>
                                <DoppeActionModalIframeContent
                                    withMinHeight={false}
                                    src={iframeModalURL}
                                />
                            </div>
                        </DoppeViewerModalPanel>
                    </BymoPageStyleScopeContext.Provider>
                )
            });
            
            // Remove the query param from URL without pushing to history
            urlParams.delete('iframeModalURL');
            const newSearch = urlParams.toString();
            const newUrl = newSearch 
                ? `${window.location.pathname}?${newSearch}${window.location.hash}`
                : `${window.location.pathname}${window.location.hash}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    // Tracks whether initial hydration has completed.
    // Starts as true, set to false after the first render's effects run.
    // Used by DoppeViewerPage to skip scroll-to-top on hydration
    // (user may have already scrolled before hydration completes).
    const isInitialHydrationRef = useRef(true);

    useEffect(() => {
        isInitialHydrationRef.current = false;
    }, []);

    const bymoPageViewerApi = useEqualityCheckedMemo<BymoPageViewerApi>({
        setDocumentScrollTop: useEventCallback(async (scrollTop: number) => {
            if (modalIsShown) {
                document.documentElement.style.setProperty(vars.verticalShift, cssAddUnitsToNumericValue(-scrollTop, 'px'));
                const siteMain = document.getElementById('SITE_MAIN');
                if (siteMain) {
                    siteMain.scrollTop = scrollTop;
                }

                lastScrollTopRef.current = scrollTop;
            } else {
                document.documentElement.scrollTop = scrollTop
            }
        }),
        isInitialHydration: useEventCallback(() => {
            return isInitialHydrationRef.current;
        }),
    }, objectShallowEqual);

    return reactProvideContextValues()
        .addContextValue(BymoPageStyleContext, bymoPageNamespacedStyleVarsValues)
        .addContextValue(BymoPageContextPropsContext, bymoPageContextProps)
        .addContextValue(DoppeViewerHtmlSiteInfoReactContext, bymoPageContextProps.htmlSiteInfo)
        .addContextValue(BymoPagePropsContext, bymoPageProps)
        .addContextValue(DoppeViewerLangReactContext, bymoPageProps.lang)
        .addContextValue(DoppeViewerShouldShowCookieBannerReactContext, bymoPageProps.showCookieBanner)
        .addContextValue(DoppeViewerColorPaletteCalculatedColorsDataReactContext, bymoPageProps.colorPaletteCalculatedColorsData)
        .addContextValue(DoppeViewerShouldShowPrivacyAndTermsReactContext, bymoPageProps.showPrivacyAndTerms)
        .addContextValue(DoppeViewerPageStandardDesktopLayoutContentWidthReactContext, bymoPageProps.standardDesktopLayoutContentWidth)
        .addContextValue(DoppeViewerPageDesktopLayoutReactContext,
            bymoTemplateDesktopLayoutEnsureSupportedLayout(
                bymoPageProps.pageLayout.layout,
                bymoPageProps.desktopLayout.layout
            )
        )
        .addContextValue(BymoPageViewerApiReactContext, bymoPageViewerApi)
        .render(
            <ListViewIdsProvider>
                <SsrSafeHtmlIdsProvider>
                    <ModalsScope
                        ref={modalsScopeRef}
                        targetContainer={() => assertNotNullable(bymoPageRootRef.current)}
                        zIndex={999}
                        modalsClassName={classes.modalView}
                        onOpenModalsChange={(openModals) => {

                            const currentWindow = (rootWindow ?? window);
                            const document = currentWindow.document;
                            const siteMain = document.getElementById('SITE_MAIN');

                            if (openModals.length > 0) {
                                const lastScrollTop = lastScrollTopRef.current = document.documentElement.scrollTop;

                                const scrollbarSize = domGetScrollbarSize();
                                document.documentElement.style.setProperty(vars.scrollbarSize, `${domGetScrollbarSize()}px`);
                                document.documentElement.style.setProperty(vars.verticalShift, `-${lastScrollTop}px`);
                                document.documentElement.classList.toggle(classes.modalShown, true);
                                document.documentElement.classList.toggle(classes.modalShownWithClassicScrollbar, scrollbarSize > 0);

                                if (siteMain) {
                                    siteMain.scrollTop = lastScrollTop;
                                }

                                if (isIos) {
                                    const visualViewportHeight = currentWindow.visualViewport?.height
                                    document.documentElement.style.setProperty(
                                        vars.visualViewportHeight,
                                        isIos && visualViewportHeight ? `${visualViewportHeight}px` : `100%`
                                    )
                                }

                                setModalIsShown(true);
                            } else {

                                const restoreScrolling = () => {
                                    if (siteMain) {
                                        siteMain.scrollTop = 0;
                                    }

                                    document.documentElement.classList.toggle(classes.modalShown, false);
                                    document.documentElement.scrollTop = lastScrollTopRef.current;
                                    setModalIsShown(false);
                                }

                                if (Features.enableBackInterception) {
                                    setTimeout(() => {
                                        restoreScrolling();
                                    }, 100)
                                } else {
                                    restoreScrolling();
                                }
                            }

                            // document.documentElement.style.setProperty(vars.scrollbarSize, `${domGetScrollbarSize()}px`);
                            // document.documentElement.classList.toggle(classes.modalsShown, openModals.find(modalInfo => modalInfo.isFullScreen) !== undefined);
                        }}
                    >
                        <BymoPageRoot
                            bymoPageProps={bymoPageProps}
                            rootRef={bymoPageRootRef}
                        >
                            {React.createElement(bymoPageComponentType, bymoPageProps)}
                        </BymoPageRoot>
                    </ModalsScope>
                </SsrSafeHtmlIdsProvider>
            </ListViewIdsProvider>
        )
});

export namespace BymoPageViewer {

    export interface Props {
        bymoPageComponentType: React.ComponentType<BymoPageProps<any>>;
        bymoPagePropsForViewer: BymoPagePropsForViewer;
        bymoPageContextProps: BymoPageContextProps;
        currentPageInfo?: DoppeViewerPageInfo; // Optional parameter for multi-page support
    }
}